/** @internal – import from `@/store/useDepartmentStore` instead. */
import type { EcommercePipelineInput } from "../types";
import { runSSEStream } from "../sseRunner";
import { buildErrorMessage } from "../utils";
import { sseEvents } from "../../../events";

/**
 * Ecommerce-research SSE pipeline runner. Posts to
 * `/core/coagent/ecommerce-research` and publishes typed `ecommerce:*`
 * events. Subscriber owns sub-phase state machine + widget highlights.
 *
 * Migrated off the `SSECallbacks` mutator-bag in Phase 8 commit 76.
 */
export async function runEcommerceResearchSSE(
  ecommerceInput: EcommercePipelineInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  await runSSEStream(ecommerceInput, {
    pipeline: "ecommerce-research",
    endpoint: "/core/coagent/ecommerce-research",
    sessionId,
    buildBody: (sid, input) => ({ sessionId: sid, ecommerceInput: input }),
    errorPrefix: "E-commerce research",
    signal,
    debugMeta: { shopDomain: ecommerceInput.shopDomain, hasAbortSignal: Boolean(signal) },
    handleEvent: (event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { phase, status, data, error, details } = event as Record<string, any>;
      const ev = data as Record<string, unknown> | undefined;

      if (phase === "session_named" && ev?.name) {
        sseEvents.publish({
          type: "ecommerce:session-named",
          pipeline: "ecommerce-research",
          sessionId,
          name: ev.name as string,
        });
        return;
      }

      // ── File extraction sub-states (manual upload) ──
      if (phase === "file_extraction") {
        if (status === "detecting_schema") {
          sseEvents.publish({
            type: "ecommerce:file-extraction-detecting-schema",
            pipeline: "ecommerce-research",
            sessionId,
          });
        }
        if (status === "extracting_products") {
          sseEvents.publish({
            type: "ecommerce:file-extraction-extracting-products",
            pipeline: "ecommerce-research",
            sessionId,
          });
        }
        if (status === "preview") {
          sseEvents.publish({
            type: "ecommerce:file-extraction-preview",
            pipeline: "ecommerce-research",
            sessionId,
            data: ev ?? {},
          });
        }
        if (status === "complete") {
          sseEvents.publish({
            type: "ecommerce:file-extraction-complete",
            pipeline: "ecommerce-research",
            sessionId,
            productCount: Number(ev?.productCount ?? 0),
          });
        }
        if (status === "error") {
          sseEvents.publish({
            type: "ecommerce:file-extraction-error",
            pipeline: "ecommerce-research",
            sessionId,
            message: typeof ev?.message === "string" ? ev.message : "File extraction failed",
          });
        }
        return;
      }

      // ── store_connect ──
      if (phase === "store_connect" && status === "running") {
        sseEvents.publish({
          type: "ecommerce:store-connect-running",
          pipeline: "ecommerce-research",
          sessionId,
          shopDomain: typeof ev?.shopDomain === "string" ? ev.shopDomain : "",
          data: ev ?? {},
        });
        return;
      }

      if (phase === "store_connect" && status === "complete") {
        sseEvents.publish({
          type: "ecommerce:store-connect-complete",
          pipeline: "ecommerce-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "store_connect" && status === "error") {
        sseEvents.publish({
          type: "ecommerce:store-connect-error",
          pipeline: "ecommerce-research",
          sessionId,
          message: typeof ev?.error === "string" ? ev.error : "Store connection failed",
        });
        return;
      }

      // ── product_import ──
      if (phase === "product_import" && status === "running") {
        sseEvents.publish({
          type: "ecommerce:product-import-running",
          pipeline: "ecommerce-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "product_import" && status === "complete") {
        sseEvents.publish({
          type: "ecommerce:product-import-complete",
          pipeline: "ecommerce-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      // ── brand_analysis ──
      if (phase === "brand_analysis" && status === "running") {
        sseEvents.publish({
          type: "ecommerce:brand-analysis-running",
          pipeline: "ecommerce-research",
          sessionId,
        });
        return;
      }

      if (phase === "brand_analysis" && status === "complete") {
        sseEvents.publish({
          type: "ecommerce:brand-analysis-complete",
          pipeline: "ecommerce-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      // ── product_categorization ──
      if (phase === "product_categorization" && status === "running") {
        sseEvents.publish({
          type: "ecommerce:product-categorization-running",
          pipeline: "ecommerce-research",
          sessionId,
        });
        return;
      }

      if (phase === "product_categorization" && status === "complete") {
        sseEvents.publish({
          type: "ecommerce:product-categorization-complete",
          pipeline: "ecommerce-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "done") {
        sseEvents.publish({
          type: "ecommerce:done",
          pipeline: "ecommerce-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "error") {
        const message = buildErrorMessage(error, details, "E-commerce research pipeline failed");
        console.error("[coagent/ecommerce-research] SSE error", { sessionId, message, details });
        sseEvents.publish({
          type: "ecommerce:error",
          pipeline: "ecommerce-research",
          sessionId,
          message,
          ...(details !== undefined ? { details } : {}),
        });
      }
    },
  });
}
